import React, { useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ArticleIcon from "@mui/icons-material/Article";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentsIcon from "@mui/icons-material/Payments";
import SavingsIcon from "@mui/icons-material/Savings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { useMsal } from "@azure/msal-react";
import { protectedResources } from "../../authConfig";

const getApiUrl = () => `${process.env.REACT_APP_BACKEND_BASE_URL}/api/form161`;

const BRAND_COLOR = "#1976d2";
const BRAND_COLOR_DARK = "#1565c0";

const HeaderCard = styled(Card)(() => ({
  marginBottom: 24,
  background: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
  boxShadow: "0 4px 20px rgba(25, 118, 210, 0.15)",
}));

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dragActive",
})(({ theme, dragActive }) => ({
  border: `2px dashed ${dragActive ? BRAND_COLOR : theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(5),
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: dragActive ? "rgba(25, 118, 210, 0.06)" : "#fafafa",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: BRAND_COLOR,
    backgroundColor: "rgba(25, 118, 210, 0.04)",
  },
}));

const SectionAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: "10px !important",
  overflow: "hidden",
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  "&:before": { display: "none" },
  border: `1px solid ${theme.palette.divider}`,
}));

const SectionSummary = styled(AccordionSummary)(() => ({
  backgroundColor: "#f0f7ff",
  "&.Mui-expanded": { borderBottom: "1px solid #e0e0e0" },
}));

const steps = ["העלאת קובץ", "בדיקת ואישור נתונים", "יצירת קובץ XML"];

// Mirrors the validation the backend enforces in buildXml.js before generating the XML -
// surfaced here too so mistakes are caught during review, not after a failed submission.
const PHONE_PATTERN = /^(05\d{8}|0[234789]\d{7}|07[2346789]\d{7})$/;
const isValidPhone = (raw) => !!raw && PHONE_PATTERN.test(String(raw).trim().replace(/[\s-]/g, ""));
const PHONE_HELPER = "מספר טלפון לא תקין (פורמט ישראלי, לדוגמה 0501234567)";

const isValidDate = (raw) => {
  if (!raw) return false;
  const str = String(raw).trim();
  return (
    /^(19|20)\d{6}$/.test(str) ||
    /^\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{4}$/.test(str) ||
    /^\d{4}[\/\-.]\d{1,2}[\/\-.]\d{1,2}$/.test(str) ||
    /^\d{8}$/.test(str)
  );
};
const DATE_HELPER = "תאריך לא תקין (פורמט DD/MM/YYYY)";

const isValidIdNumber = (raw) => !!raw && /^\d+$/.test(String(raw).trim()) && String(raw).trim().length <= 9;
const ID_HELPER = "מספר זהות לא תקין (עד 9 ספרות)";

/** Simple labeled text input bound to a `{ label, value }` cell in the extracted data. */
function FieldInput({ cell, onChange, gridSize = 6, error, helperText }) {
  if (!cell) return null;
  return (
    <Grid item xs={12} sm={gridSize}>
      <TextField
        fullWidth
        size="small"
        label={cell.label}
        value={cell.value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        helperText={error ? helperText : undefined}
      />
    </Grid>
  );
}

/** Per-field-key validation for the employer/employee cell grids. `required: false` fields are
 * only flagged once filled in (they're allowed to stay empty, per the XSD's minOccurs="0"). */
const FIELD_VALIDATORS = {
  contactMobileNumber: { isValid: isValidPhone, helperText: PHONE_HELPER, required: true },
  employeeMobileNumber: { isValid: isValidPhone, helperText: PHONE_HELPER, required: false },
  employeeIdNumber: { isValid: isValidIdNumber, helperText: ID_HELPER, required: true },
  employmentStartDate: { isValid: isValidDate, helperText: DATE_HELPER, required: true },
  employmentRetirementDate: { isValid: isValidDate, helperText: DATE_HELPER, required: true },
};

const isFieldInvalid = (validator, value) =>
  !!validator && (validator.required ? !validator.isValid(value) : !!value && !validator.isValid(value));

function SectionHeader({ icon, title, count }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {typeof count === "number" && (
        <Chip label={count} size="small" sx={{ backgroundColor: "rgba(25,118,210,0.12)", color: BRAND_COLOR, fontWeight: 600 }} />
      )}
    </Box>
  );
}

export default function Form161Page() {
  const msal = useMsal();
  const { execute: executeWrite } = useFetchWithMsal({
    scopes: [
      ...protectedResources.apiScopes.scopes.read,
      ...protectedResources.apiScopes.scopes.write,
    ],
  });

  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const activeStep = data ? 2 : file ? 1 : 0;

  const applyFile = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") {
      setError("יש להעלות קובץ PDF בלבד");
      return;
    }
    setFile(f);
    setError(null);
    setData(null);
    setSuccess(null);
  };

  const handleFileChange = (e) => applyFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    applyFile(e.dataTransfer.files[0]);
  };

  const handleExtract = async () => {
    if (!file) {
      setError("נא לבחור קובץ PDF");
      return;
    }
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await executeWrite("POST", `${getApiUrl()}/extract`, formData);
      setData(result);
    } catch (err) {
      setError(err.msg || err.message || "שגיאה בקריאת הטופס");
    } finally {
      setLoading(false);
    }
  };

  const updateEmployee = (key, value) =>
    setData((prev) => ({ ...prev, employee: { ...prev.employee, [key]: { ...prev.employee[key], value } } }));

  const updateEmployer = (key, value) =>
    setData((prev) => ({ ...prev, employer: { ...prev.employer, [key]: { ...prev.employer[key], value } } }));

  const updateListRow = (listName, index, field, value) =>
    setData((prev) => {
      const list = [...prev[listName]];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [listName]: list };
    });

  const handleGenerateXml = async () => {
    setError(null);
    try {
      const { instance, accounts } = msal;
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: protectedResources.apiScopes.scopes.write,
        account: accounts[0],
      });
      const response = await fetch(`${getApiUrl()}/generate-xml`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const text = await response.text();
        let message = text || "שגיאה ביצירת קובץ ה-XML";
        try {
          const parsed = JSON.parse(text);
          message = parsed.details || parsed.error || message;
        } catch {
          // response wasn't JSON - fall back to raw text
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const firstName = data?.employee?.firstName?.value?.trim();
      const lastName = data?.employee?.lastName?.value?.trim();
      const employeeName = [firstName, lastName].filter(Boolean).join("_").replace(/[^\p{L}\p{N}_-]/gu, "");
      link.download = employeeName ? `form161_${employeeName}.xml` : "form161.xml";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      setSuccess("קובץ ה-XML נוצר והורד בהצלחה");
    } catch (err) {
      setError(err.message || "שגיאה ביצירת קובץ ה-XML");
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1100, mx: "auto" }}>
      <HeaderCard>
        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: BRAND_COLOR,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ArticleIcon sx={{ color: "white", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_COLOR_DARK }}>
              המרת טופס 161 ל-XML לדיווח לרשות המסים
            </Typography>
            {/* <Typography variant="body2" sx={{ color: "#00695C", opacity: 0.85, mt: 0.5 }}>
              העלאת PDF של טופס 161 (חלק א'), אישור/תיקון הנתונים שחולצו, ויצירת קובץ XML תקני
            </Typography> */}
          </Box>
        </CardContent>
      </HeaderCard>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <input
            ref={fileInputRef}
            style={{ display: "none" }}
            id="form161-file-upload"
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
          />
          <DropZone
            dragActive={dragActive}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <>
                <InsertDriveFileIcon sx={{ fontSize: 44, color: BRAND_COLOR, mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  לחצי כדי לבחור קובץ אחר
                </Typography>
              </>
            ) : (
              <>
                <UploadFileIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  גררי לכאן קובץ PDF או לחצי לבחירה
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  טופס 161 - חלק א' בלבד
                </Typography>
              </>
            )}
          </DropZone>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleExtract}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArticleIcon />}
              sx={{ backgroundColor: BRAND_COLOR, "&:hover": { backgroundColor: BRAND_COLOR_DARK }, px: 4 }}
            >
              {loading ? "מחלץ נתונים..." : "חלץ נתונים מהקובץ"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {data && (
        <>
          <SectionAccordion defaultExpanded>
            <SectionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionHeader icon={<BusinessIcon sx={{ color: BRAND_COLOR }} />} title="פרטי המעסיק" />
            </SectionSummary>
            <AccordionDetails sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                {Object.entries(data.employer).map(([key, cell]) => {
                  const validator = FIELD_VALIDATORS[key];
                  const invalid = isFieldInvalid(validator, cell?.value);
                  return (
                    <FieldInput
                      key={key}
                      cell={cell}
                      onChange={(v) => updateEmployer(key, v)}
                      gridSize={4}
                      error={invalid}
                      helperText={validator?.helperText}
                    />
                  );
                })}
              </Grid>
            </AccordionDetails>
          </SectionAccordion>

          <SectionAccordion defaultExpanded>
            <SectionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionHeader icon={<PersonIcon sx={{ color: BRAND_COLOR }} />} title="פרטי העובד" />
            </SectionSummary>
            <AccordionDetails sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                {(() => {
                  // FullLastSalaryBeforeRetirement and LastInsuredSalary are the same figure on
                  // these forms in the overwhelming majority of cases - mirror one into the other
                  // when only one was filled in (matches the fallback in buildXml.js).
                  const fullSalary = data.employee.fullLastSalaryBeforeRetirement?.value;
                  const insuredSalary = data.employee.lastInsuredSalary?.value;
                  const salaryMirror = {
                    fullLastSalaryBeforeRetirement: {
                      value: fullSalary || insuredSalary || "",
                      copied: !fullSalary && !!insuredSalary,
                    },
                    lastInsuredSalary: {
                      value: insuredSalary || fullSalary || "",
                      copied: !insuredSalary && !!fullSalary,
                    },
                  };

                  return [
                    "employeeIdNumber",
                    "firstName",
                    "lastName",
                    "employeeEmail",
                    "employeeMobileNumber",
                    "holderOfControllingInterest",
                    "relativeHolderOfControllingInterest",
                    "retirmentReason",
                    "employmentStartDate",
                    "employmentRetirementDate",
                    "fullLastSalaryBeforeRetirement",
                    "lastInsuredSalary",
                    "severanceSalary",
                  ].map((key) => {
                    const validator = FIELD_VALIDATORS[key];
                    const mirror = salaryMirror[key];
                    const cell = mirror
                      ? {
                          ...data.employee[key],
                          value: mirror.value,
                          label: `${data.employee[key]?.label ?? ""}${mirror.copied ? " - הושלם אוטומטית" : ""}`,
                        }
                      : data.employee[key];
                    const invalid = isFieldInvalid(validator, cell?.value);
                    return (
                      <FieldInput
                        key={key}
                        cell={cell}
                        onChange={(v) => updateEmployee(key, v)}
                        gridSize={4}
                        error={invalid}
                        helperText={validator?.helperText}
                      />
                    );
                  });
                })()}
              </Grid>
              <Alert severity="info" variant="outlined" sx={{ mt: 3 }}>
                בעל שליטה/קרוב: 1=כן, 2=לא &nbsp;|&nbsp; סיבת פרישה: 1=גמלאות/פיטורין/התפטרות, 2=פטירה, 3=נכות 75%+
                &nbsp;|&nbsp; תאריכים בפורמט YYYYMMDD
              </Alert>
            </AccordionDetails>
          </SectionAccordion>

          {data.workPeriods.length > 0 && (
            <SectionAccordion>
              <SectionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader
                  icon={<EventRepeatIcon sx={{ color: BRAND_COLOR }} />}
                  title="תקופות עבודה לא רציפות"
                  count={data.workPeriods.length}
                />
              </SectionSummary>
              <AccordionDetails>
                <SimpleTable
                  rows={data.workPeriods}
                  listName="workPeriods"
                  columns={[
                    { key: "startDate", label: "תאריך התחלה", isValid: isValidDate, helperText: DATE_HELPER },
                    { key: "endDate", label: "תאריך סיום", isValid: isValidDate, helperText: DATE_HELPER },
                    { key: "employmentRate", label: "שיעור משרה" },
                    { key: "lastSalary", label: "משכורת אחרונה לתקופה" },
                  ]}
                  onChange={updateListRow}
                />
              </AccordionDetails>
            </SectionAccordion>
          )}

          {data.budgetaryPension && (
            <SectionAccordion>
              <SectionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader icon={<SavingsIcon sx={{ color: BRAND_COLOR }} />} title="פנסיה תקציבית" />
              </SectionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="תאריך זכאות"
                      value={data.budgetaryPension.entitlementDate}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          budgetaryPension: { ...prev.budgetaryPension, entitlementDate: e.target.value },
                        }))
                      }
                      error={!isValidDate(data.budgetaryPension.entitlementDate)}
                      helperText={!isValidDate(data.budgetaryPension.entitlementDate) ? DATE_HELPER : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="משכורת לחישוב הפנסיה"
                      value={data.budgetaryPension.salaryForCalculation}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          budgetaryPension: { ...prev.budgetaryPension, salaryForCalculation: e.target.value },
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </SectionAccordion>
          )}

          {data.retirementInstallments.length > 0 && (
            <SectionAccordion>
              <SectionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader
                  icon={<PaymentsIcon sx={{ color: BRAND_COLOR }} />}
                  title="מענקי פרישה לשיעורין"
                  count={data.retirementInstallments.length}
                />
              </SectionSummary>
              <AccordionDetails>
                <SimpleTable
                  rows={data.retirementInstallments}
                  listName="retirementInstallments"
                  columns={[
                    { key: "paymentDate", label: "תאריך תשלום" },
                    { key: "paymentAmount", label: "סכום" },
                  ]}
                  onChange={updateListRow}
                />
              </AccordionDetails>
            </SectionAccordion>
          )}

          {data.nonTaxExemptGrants.length > 0 && (
            <SectionAccordion defaultExpanded>
              <SectionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader
                  icon={<AccountBalanceIcon sx={{ color: BRAND_COLOR }} />}
                  title="פירוט המענקים (א9.)"
                  count={data.nonTaxExemptGrants.length}
                />
              </SectionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  שדה "תאריך הפקדה ראשון" מגיע משני חלקים נפרדים בטופס המקורי - אנא ודאו שהערך המאושר נכון.
                </Alert>
                {data.nonTaxExemptGrants.map((g, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{ mb: 3, p: 3, borderRadius: 2, "&:last-child": { mb: 0 } }}
                  >
                    <Chip
                      label={`שורה ${i + 1}`}
                      size="small"
                      sx={{ mb: 2, backgroundColor: "rgba(25,118,210,0.12)", color: BRAND_COLOR, fontWeight: 600 }}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="שם המשלם" value={g.payerName}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "payerName", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="מספר תיק ניכויים" value={g.deductionFileNumber}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "deductionFileNumber", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="קוד המשלם" value={g.payerTypeCode}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "payerTypeCode", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="סוג התשלום" value={g.paymentType}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "paymentType", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="תאריך הפקדה ראשון - חלק 1 (גולמי)" value={g.firstDepositDatePart1}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "firstDepositDatePart1", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="תאריך הפקדה ראשון - חלק 2 (גולמי)" value={g.firstDepositDatePart2}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "firstDepositDatePart2", e.target.value)} />
                      </Grid>
                      {(() => {
                        // Mirrors the FirstDepositDate/LastDepositDate fallback in buildXml.js,
                        // so the reviewer sees the same auto-filled value that will be submitted.
                        const firstDepositMerged = g.firstDepositDate || `${g.firstDepositDatePart1 ?? ""}${g.firstDepositDatePart2 ?? ""}`;
                        const firstFilled = firstDepositMerged?.trim();
                        const lastFilled = g.lastDepositDate?.trim();
                        const effectiveFirst = firstFilled || lastFilled || "";
                        const effectiveLast = lastFilled || (firstFilled ? firstDepositMerged : "");
                        const firstCopied = !firstFilled && !!lastFilled;
                        const lastCopied = !lastFilled && !!firstFilled;
                        return (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label={`תאריך הפקדה ראשון - מאושר (YYYYMMDD)${firstCopied ? " - הושלם אוטומטית מהתאריך האחרון" : ""}`}
                                value={effectiveFirst}
                                onChange={(e) => updateListRow("nonTaxExemptGrants", i, "firstDepositDate", e.target.value)}
                                sx={{ "& .MuiInputBase-input": { fontWeight: 600 } }}
                                error={!isValidDate(effectiveFirst)}
                                helperText={!isValidDate(effectiveFirst) ? DATE_HELPER : undefined}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label={`תאריך הפקדה אחרון לקופה${lastCopied ? " - הושלם אוטומטית מהתאריך הראשון" : ""}`}
                                value={effectiveLast}
                                onChange={(e) => updateListRow("nonTaxExemptGrants", i, "lastDepositDate", e.target.value)}
                                error={!!effectiveLast && !isValidDate(effectiveLast)}
                                helperText={!!effectiveLast && !isValidDate(effectiveLast) ? DATE_HELPER : undefined}
                              />
                            </Grid>
                          </>
                        );
                      })()}
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="הסכום ליום הפרישה" value={g.amountForRetirementDay}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "amountForRetirementDay", e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="צבירה נוספת" value={g.additionalAccumulation}
                          onChange={(e) => updateListRow("nonTaxExemptGrants", i, "additionalAccumulation", e.target.value)} />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </AccordionDetails>
            </SectionAccordion>
          )}

          {data.taxExemptGrants.length > 0 && (
            <SectionAccordion>
              <SectionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader
                  icon={<AccountBalanceIcon sx={{ color: BRAND_COLOR }} />}
                  title="הפקדות שחויבו במס (א10.)"
                  count={data.taxExemptGrants.length}
                />
              </SectionSummary>
              <AccordionDetails>
                <SimpleTable
                  rows={data.taxExemptGrants}
                  listName="taxExemptGrants"
                  columns={[
                    { key: "payerName", label: "שם המשלם" },
                    { key: "deductionFileNumber", label: "תיק ניכויים" },
                    { key: "nominalAmount", label: "סכום נומינלי" },
                    { key: "realAmount", label: "סכום ריאלי" },
                  ]}
                  onChange={updateListRow}
                />
              </AccordionDetails>
            </SectionAccordion>
          )}

          {data.preRetirementPension.length > 0 && (
            <SectionAccordion>
              <SectionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader
                  icon={<SavingsIcon sx={{ color: BRAND_COLOR }} />}
                  title="קצבה טרם הפרישה (א11.)"
                  count={data.preRetirementPension.length}
                />
              </SectionSummary>
              <AccordionDetails>
                <SimpleTable
                  rows={data.preRetirementPension}
                  listName="preRetirementPension"
                  columns={[
                    { key: "payerName", label: "שם המשלם" },
                    { key: "deductionFileNumber", label: "תיק ניכויים" },
                    { key: "payerTypeCode", label: "קוד המשלם" },
                    { key: "pensionConversionDate", label: "חודש המרה", isValid: isValidDate, helperText: DATE_HELPER },
                    { key: "pensionAmount", label: "סכום קצבה" },
                    { key: "convertionAmountBallance", label: "סכום שעל בסיסו חושבה" },
                  ]}
                  onChange={updateListRow}
                />
              </AccordionDetails>
            </SectionAccordion>
          )}

          <Card
            sx={{
              mt: 3,
              position: "sticky",
              bottom: 16,
              boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon sx={{ color: BRAND_COLOR }} />
                <Typography variant="body2" color="text.secondary">
                  לאחר בדיקה ואישור, ניתן ליצור את קובץ ה-XML הסופי
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleGenerateXml}
                sx={{ backgroundColor: BRAND_COLOR, "&:hover": { backgroundColor: BRAND_COLOR_DARK }, px: 4 }}
              >
                צור והורד קובץ XML
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

function SimpleTable({ rows, listName, columns, onChange }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
            {columns.map((c) => (
              <TableCell key={c.key} sx={{ fontWeight: 600 }}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} hover>
              {columns.map((c) => {
                const value = row[c.key] ?? "";
                const invalid = c.isValid && value && !c.isValid(value);
                return (
                  <TableCell key={c.key}>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      value={value}
                      onChange={(e) => onChange(listName, i, c.key, e.target.value)}
                      error={!!invalid}
                      helperText={invalid ? c.helperText : undefined}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
